import { createMiddleware, createStart } from '@tanstack/react-start';
import { APP_VERSION_HEADER, syncVersionFromHeader } from '@/lib/app-version';
import { telemetryHeaders } from '@/lib/telemetry-headers';
import { cutoverRedirectForRequest } from '@/lib/cutover-redirect';

/**
 * Merged, not replaced: TanStack Start sets its own headers. Guarded because `Headers.set` throws on a bad
 * value, `x-app-version` comes from localStorage, and this is on every server-function call.
 */
function withTelemetryHeaders(init: RequestInit | undefined): RequestInit | undefined {
	try {
		const extra = telemetryHeaders();
		if (Object.keys(extra).length === 0) return init;
		const headers = new Headers(init?.headers);
		for (const [name, value] of Object.entries(extra)) headers.set(name, value);
		return { ...init, headers };
	} catch {
		return init;
	}
}

const appVersionAwareFetch = Object.assign((...args: Parameters<typeof fetch>) => {
	const [input, init] = args;
	return fetch(input, withTelemetryHeaders(init)).then((response) => {
		syncVersionFromHeader(response);
		return response;
	});
}, fetch);

const appVersionHeaderMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const result = await options.next();

	if (result?.response?.headers) {
		const deployedVersion = process.env.VITE_APP_VERSION || 'dev-local';
		result.response.headers.set(APP_VERSION_HEADER, deployedVersion);
	}
	return result;
});

// Imported inside `.server()`: this instance is isomorphic, and `wide-event.server` pulls in sst.
const requestWideEventMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	const wide = await import('@/lib/wide-event.server');
	return wide.instrumentPortalsRequest(() => Promise.resolve(options.next()));
});

/** The cutover 301s (TEC-3770), here because `rewrite.input` folds the host away before any route. */
const cutoverRedirectMiddleware = createMiddleware({ type: 'request' }).server(async (options) => {
	// A server function is a call, not a navigation: a 301 on a POST is a lost call.
	if (options.handlerType !== 'router') return options.next();

	const location = cutoverRedirectForRequest(options.request);
	if (location) return new Response(null, { status: 301, headers: { Location: location } });
	return options.next();
});

export const startInstance = createStart(() => ({
	// Wide event FIRST: only the outermost entry sees the status the caller got, the 301 included.
	requestMiddleware: [requestWideEventMiddleware, cutoverRedirectMiddleware, appVersionHeaderMiddleware],
	serverFns: {
		fetch: appVersionAwareFetch,
	},
}));
